import * as THREE from 'three';
import type { Brand } from '../brand/types';
import { Rng } from '../sim/rng';
import type { Tunables } from '../sim/tunables';

export interface StadiumView {
  group: THREE.Group;
  /** 0 calm, 1 on its feet. Eases toward the target each frame. */
  setExcitement(level: number): void;
  update(dt: number): void;
}

/**
 * Night stadium (GDD section 10): two tiers of stands around the field with a generated crowd in the
 * brand colours, a brand fascia, LED sideline boards, four light towers with glow, and a gradient sky.
 * Everything is generated at startup from brand.json; no image files.
 */
export function createStadium(brand: Brand, t: Tunables, logo: HTMLImageElement | null, maxAnisotropy: number): StadiumView {
  const anisotropy = Math.min(maxAnisotropy, t.render.anisotropy);
  const group = new THREE.Group();
  const f = t.field;
  const s = t.stadium;
  const halfW = f.widthYards / 2 + f.wallOffsetYards;
  const halfL = f.lengthYards / 2 + f.wallOffsetYards;

  group.add(sky(t));

  // Crowd material. The texture holds the same fans twice: seated (top half) and on their feet with their
  // arms up (bottom half). Each fan picks a half from its own hash and the excitement, so the crowd rises
  // fan by fan as the play gets bigger; each also bobs on its own beat. Tiles are mirrored at random and
  // vary a little in brightness so the repeat does not show.
  const crowdTex = crowdTexture(brand, t);
  crowdTex.anisotropy = anisotropy;
  const uniforms = { uTime: { value: 0 }, uCheer: { value: 0 } };
  const crowdMat = new THREE.MeshLambertMaterial({ map: crowdTex });
  const cols = s.crowdCols.toFixed(1);
  const rows = (s.crowdRows / 2).toFixed(1);
  const glsl = (v: number) => v.toFixed(4);
  crowdMat.onBeforeCompile = (shader) => {
    shader.uniforms['uTime'] = uniforms.uTime;
    shader.uniforms['uCheer'] = uniforms.uCheer;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        uniform float uTime;
        uniform float uCheer;
        float crowdHash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }`,
      )
      .replace(
        '#include <map_fragment>',
        `vec2 tile = floor(vMapUv);
        vec2 f = fract(vMapUv);
        float th = crowdHash(tile);
        if (th > 0.5) f.x = 1.0 - f.x;
        vec2 cell = floor(f * vec2(${cols}, ${rows}));
        float h = crowdHash(cell + tile * 41.0);
        float standing = step(h, ${glsl(s.crowdStandingShare)} + uCheer * ${glsl(s.crowdCheerStandShare)});
        float bob = sin(uTime * ${glsl(s.crowdBounceHz * 2 * Math.PI)} * (1.0 + uCheer) + h * 6.2832) * (${glsl(s.crowdBounceCalm)} + ${glsl(s.crowdBounceCheer)} * uCheer);
        float ry = clamp(fract(f.y * ${rows}) - bob, 0.0, 0.999);
        float v = (cell.y + ry) / ${rows};
        vec2 suv = vec2(f.x, mix(0.5 + v * 0.5, v * 0.5, standing));
        vec2 gx = dFdx(vMapUv) * vec2(1.0, 0.5);
        vec2 gy = dFdy(vMapUv) * vec2(1.0, 0.5);
        vec4 sampledDiffuseColor = textureGrad(map, suv, gx, gy);
        diffuseColor *= sampledDiffuseColor * (${glsl(1 - s.crowdTileShade)} + ${glsl(2 * s.crowdTileShade)} * th);`,
      );
  };

  const concrete = new THREE.MeshLambertMaterial({ color: t.look.standConcrete });
  const fasciaTex = fasciaTexture(brand, logo, t);
  fasciaTex.anisotropy = anisotropy;

  // Sideline stands (along z) and end stands (along x), two tiers each.
  const sides: { len: number; pos: (d: number, y: number) => [number, number, number]; rotY: number }[] = [
    { len: 2 * halfL + 30, pos: (d, y) => [halfW + d, y, 0], rotY: -Math.PI / 2 },
    { len: 2 * halfL + 30, pos: (d, y) => [-halfW - d, y, 0], rotY: Math.PI / 2 },
    { len: 2 * halfW + 30, pos: (d, y) => [0, y, -halfL - d], rotY: 0 },
    { len: 2 * halfW + 30, pos: (d, y) => [0, y, halfL + d], rotY: Math.PI },
  ];
  for (const side of sides) {
    let base = s.firstTierGapYards;
    let y0 = f.wallHeightYards;
    for (let tier = 0; tier < 2; tier++) {
      const depth = s.tierDepthYards * (tier === 0 ? 1 : 0.8);
      const rise = s.tierRiseYards * (tier === 0 ? 1 : 1.1);
      const slope = Math.hypot(depth, rise);
      const tex = crowdTex.clone();
      tex.repeat.set(side.len / 14, s.standRows * (tier === 0 ? 1 : 0.8));
      tex.needsUpdate = true;
      const mat = crowdMat.clone();
      mat.map = tex;
      mat.onBeforeCompile = crowdMat.onBeforeCompile;
      const stand = new THREE.Mesh(new THREE.PlaneGeometry(side.len, slope), mat);
      const [x, y, z] = side.pos(base + depth / 2, y0 + rise / 2);
      stand.position.set(x, y, z);
      stand.rotation.order = 'YXZ';
      stand.rotation.y = side.rotY;
      stand.rotation.x = -(Math.PI / 2 - Math.atan2(rise, depth));
      group.add(stand);
      // Front wall of the tier.
      const front = new THREE.Mesh(new THREE.PlaneGeometry(side.len, y0 + 0.01), concrete);
      const [fx, fy, fz] = side.pos(base, y0 / 2);
      front.position.set(fx, fy, fz);
      front.rotation.y = side.rotY;
      group.add(front);
      // Fascia band (brand) between tiers.
      if (tier === 0) {
        const ft = fasciaTex.clone();
        ft.repeat.set(side.len / 40, 1);
        ft.needsUpdate = true;
        const fascia = new THREE.Mesh(new THREE.PlaneGeometry(side.len, s.fasciaHeightYards), new THREE.MeshBasicMaterial({ map: ft }));
        const [ax, ay, az] = side.pos(base + depth + s.upperGapYards, y0 + rise + s.fasciaHeightYards / 2 + 0.5);
        fascia.position.set(ax, ay, az);
        fascia.rotation.y = side.rotY;
        group.add(fascia);
      }
      base += depth + s.upperGapYards;
      y0 += rise + s.fasciaHeightYards + 1;
    }
  }

  group.add(sidelineBoards(brand, logo, t, anisotropy));
  group.add(lightTowers(t, halfW, halfL));

  let cheer = 0;
  let cheerTarget = 0;
  return {
    group,
    setExcitement(level) {
      cheerTarget = Math.max(0, Math.min(1, level));
    },
    update(dt) {
      uniforms.uTime.value += dt;
      cheer += (cheerTarget - cheer) * (1 - Math.exp(-3 * dt));
      uniforms.uCheer.value = cheer;
    },
  };
}

function sky(t: Tunables): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      top: { value: new THREE.Color(t.look.skyTop) },
      horizon: { value: new THREE.Color(t.look.skyHorizon) },
    },
    vertexShader: 'varying vec3 vP; void main(){ vP = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader:
      'uniform vec3 top; uniform vec3 horizon; varying vec3 vP; void main(){ float h = clamp(vP.y * 2.2, 0.0, 1.0); gl_FragColor = vec4(mix(horizon, top, pow(h, 0.6)), 1.0); }',
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(t.camera.far * 0.9, 24, 12), mat);
  mesh.renderOrder = -10;
  return mesh;
}

interface Fan {
  x: number;
  shirt: string;
  skin: string;
  /** Hair or cap colour, or null for none. */
  top: string | null;
  cap: boolean;
  scarf: string | null;
  clap: boolean;
  shade: number;
}

/**
 * Rows of fans, drawn twice: seated in the top half, standing with arms up in the bottom half (the crowd
 * shader picks per fan). Some fans wear the brand colours; most wear ordinary clothes, so the stands read as
 * people rather than a pattern.
 */
function crowdTexture(brand: Brand, t: Tunables): THREE.CanvasTexture {
  const s = t.stadium;
  const W = s.crowdTilePx;
  const H = s.crowdTilePx;
  const half = H / 2;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d')!;
  const rng = new Rng(s.crowdSeed);
  const rows = s.crowdRows / 2;
  const cols = s.crowdCols;
  const cw = W / cols;
  const rh = half / rows;
  const brandCols = [brand.colors.accent, brand.colors.primary, brand.colors.primary, brand.colors.text];
  const plain = t.look.crowdClothes;
  const hair = t.look.crowdHair;
  const fans: (Fan | null)[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: (Fan | null)[] = [];
    for (let k = 0; k < cols; k++) {
      if (rng.next() < s.crowdEmptyShare) {
        row.push(null);
        continue;
      }
      const branded = rng.next() < s.crowdBrandShare;
      const capped = rng.next() < 0.22;
      row.push({
        x: k * cw + cw / 2 + rng.range(-cw * 0.08, cw * 0.08),
        shirt: branded ? brandCols[rng.int(0, brandCols.length - 1)]! : plain[rng.int(0, plain.length - 1)]!,
        skin: t.look.skinTones[rng.int(0, t.look.skinTones.length - 1)]!,
        top: capped ? (rng.next() < 0.5 ? brand.colors.accent : plain[rng.int(0, plain.length - 1)]!) : rng.next() < 0.85 ? hair[rng.int(0, hair.length - 1)]! : null,
        cap: capped,
        scarf: rng.next() < 0.18 ? (rng.next() < 0.7 ? brand.colors.accent : brand.colors.text) : null,
        clap: rng.next() < 0.12,
        shade: rng.range(0.78, 1.05),
      });
    }
    fans.push(row);
  }

  for (const standing of [false, true]) {
    const y0 = standing ? half : 0;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, y0, W, half);
    ctx.clip();
    ctx.fillStyle = t.look.standShadow;
    ctx.fillRect(0, y0, W, half);
    // Back rows first, so a fan's head overlaps the row behind.
    for (let r = 0; r < rows; r++) {
      const top = y0 + r * rh;
      // Seat backs.
      ctx.fillStyle = t.look.standConcrete;
      ctx.fillRect(0, top + rh * 0.82, W, rh * 0.18);
      for (const fan of fans[r]!) {
        if (fan) drawFan(ctx, fan, top, cw, rh, standing);
      }
    }
    ctx.restore();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function drawFan(ctx: CanvasRenderingContext2D, f: Fan, top: number, cw: number, rh: number, standing: boolean): void {
  const lift = standing ? rh * 0.14 : 0;
  const x = f.x;
  const shoulder = top + rh * 0.42 - lift;
  const headY = shoulder - rh * 0.16;
  const headR = cw * 0.17;
  const bodyW = cw * 0.74;
  // Torso, lit from above.
  const g = ctx.createLinearGradient(0, shoulder, 0, top + rh);
  g.addColorStop(0, shadeHex(f.shirt, f.shade * 1.15));
  g.addColorStop(1, shadeHex(f.shirt, f.shade * 0.55));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.roundRect(x - bodyW / 2, shoulder, bodyW, top + rh - shoulder, [cw * 0.2, cw * 0.2, 0, 0]);
  ctx.fill();
  // Arms: up when standing, a few clapping when seated.
  ctx.lineCap = 'round';
  ctx.lineWidth = cw * 0.13;
  if (standing) {
    const spread = cw * 0.32;
    const reach = rh * 0.5;
    ctx.strokeStyle = shadeHex(f.shirt, f.shade);
    ctx.beginPath();
    ctx.moveTo(x - bodyW * 0.38, shoulder + rh * 0.05);
    ctx.lineTo(x - spread, shoulder - reach);
    ctx.moveTo(x + bodyW * 0.38, shoulder + rh * 0.05);
    ctx.lineTo(x + spread, shoulder - reach);
    ctx.stroke();
    if (f.scarf) {
      ctx.strokeStyle = f.scarf;
      ctx.lineWidth = rh * 0.09;
      ctx.beginPath();
      ctx.moveTo(x - spread, shoulder - reach);
      ctx.lineTo(x + spread, shoulder - reach);
      ctx.stroke();
    }
    ctx.fillStyle = f.skin;
    for (const sx of [-spread, spread]) {
      ctx.beginPath();
      ctx.arc(x + sx, shoulder - reach, cw * 0.075, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (f.scarf) {
    ctx.fillStyle = f.scarf;
    ctx.fillRect(x - bodyW * 0.3, shoulder, bodyW * 0.6, rh * 0.08);
  } else if (f.clap) {
    ctx.fillStyle = f.skin;
    ctx.beginPath();
    ctx.arc(x, shoulder + rh * 0.16, cw * 0.08, 0, Math.PI * 2);
    ctx.fill();
  }
  // Head, then hair or a cap.
  ctx.fillStyle = shadeHex(f.skin, f.shade);
  ctx.beginPath();
  ctx.arc(x, headY, headR, 0, Math.PI * 2);
  ctx.fill();
  if (f.top) {
    ctx.fillStyle = f.top;
    ctx.beginPath();
    ctx.arc(x, headY - headR * 0.15, headR * 1.02, Math.PI, Math.PI * 2);
    ctx.fill();
    if (f.cap) ctx.fillRect(x - headR * 1.1, headY - headR * 0.25, headR * 2.2, headR * 0.3);
  }
}

/** A #rrggbb colour scaled by k (k above 1 lightens, clamped). */
function shadeHex(hex: string, k: number): string {
  const n = parseInt(hex.slice(1, 7), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v * k)));
  return `rgb(${ch((n >> 16) & 255)} ${ch((n >> 8) & 255)} ${ch(n & 255)})`;
}

function drawLogo(ctx: CanvasRenderingContext2D, logo: HTMLImageElement | null, x: number, y: number, h: number): number {
  if (!logo || !logo.naturalWidth) return 0;
  const w = (logo.naturalWidth / logo.naturalHeight) * h;
  ctx.drawImage(logo, x, y, w, h);
  return w;
}

function fasciaTexture(brand: Brand, logo: HTMLImageElement | null, t: Tunables): THREE.CanvasTexture {
  const k = t.stadium.signTextureScale;
  const c = document.createElement('canvas');
  c.width = 1024 * k;
  c.height = 64 * k;
  const ctx = c.getContext('2d')!;
  // Drawn in the 1024 x 64 layout, at k times the pixels.
  ctx.scale(k, k);
  ctx.fillStyle = brand.colors.primary;
  ctx.fillRect(0, 0, 1024, 64);
  ctx.fillStyle = brand.colors.accent;
  ctx.fillRect(0, 0, 1024, 5);
  ctx.fillRect(0, 59, 1024, 5);
  ctx.font = `800 italic 34px ${t.look.numberFont}`;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = brand.colors.accent;
  ctx.fillText(brand.tagline.toUpperCase(), 40, 33);
  drawLogo(ctx, logo, 640, 12, 40);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  return tex;
}

/** LED boards on the field wall: logo and team name on the brand primary. */
function sidelineBoards(brand: Brand, logo: HTMLImageElement | null, t: Tunables, anisotropy: number): THREE.Group {
  const f = t.field;
  const g = new THREE.Group();
  const k = t.stadium.signTextureScale;
  const c = document.createElement('canvas');
  c.width = 512 * k;
  c.height = 64 * k;
  const ctx = c.getContext('2d')!;
  // Drawn in the 512 x 64 layout, at k times the pixels.
  ctx.scale(k, k);
  ctx.fillStyle = brand.colors.primary;
  ctx.fillRect(0, 0, 512, 64);
  ctx.fillStyle = brand.colors.accent;
  ctx.fillRect(0, 58, 512, 6);
  const w = drawLogo(ctx, logo, 24, 10, 40);
  ctx.font = `800 italic 36px ${t.look.numberFont}`;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = brand.colors.text;
  ctx.fillText(brand.teamName.toUpperCase(), 48 + w, 32);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = anisotropy;
  tex.wrapS = THREE.RepeatWrapping;
  const halfW = f.widthYards / 2 + f.wallOffsetYards - 0.05;
  const halfL = f.lengthYards / 2 + f.wallOffsetYards - 0.05;
  const h = t.stadium.boardHeightYards;
  const runs: [number, number, number, number][] = [
    [2 * halfL, halfW, 0, -Math.PI / 2],
    [2 * halfL, -halfW, 0, Math.PI / 2],
    [2 * halfW, 0, -halfL, 0],
    [2 * halfW, 0, halfL, Math.PI],
  ];
  for (const [len, x, z, rotY] of runs) {
    const tx = tex.clone();
    tx.repeat.set(len / t.stadium.boardEveryYards, 1);
    tx.needsUpdate = true;
    const m = new THREE.Mesh(new THREE.PlaneGeometry(len, h), new THREE.MeshBasicMaterial({ map: tx }));
    m.position.set(x, h / 2 + 0.05, z);
    m.rotation.y = rotY;
    g.add(m);
  }
  return g;
}

function lightTowers(t: Tunables, halfW: number, halfL: number): THREE.Group {
  const s = t.stadium;
  const g = new THREE.Group();
  const poleMat = new THREE.MeshLambertMaterial({ color: t.look.standConcrete });
  const panelMat = new THREE.MeshBasicMaterial({ color: t.look.lightPanel });
  const glowTex = glowTexture();
  const glowMat = new THREE.SpriteMaterial({ map: glowTex, color: t.look.lightGlow, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, fog: false });
  const x = halfW + s.tierDepthYards * 1.6;
  const z = halfL - s.towerInsetYards;
  for (const [sx, sz] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 1, s.towerHeightYards, 6), poleMat);
    pole.position.set(sx * x, s.towerHeightYards / 2, sz * z);
    g.add(pole);
    const panel = new THREE.Mesh(new THREE.BoxGeometry(12, 6, 0.6), panelMat);
    panel.position.set(sx * x, s.towerHeightYards + 2, sz * z);
    panel.lookAt(0, 0, 0);
    g.add(panel);
    const glow = new THREE.Sprite(glowMat);
    glow.position.copy(panel.position);
    glow.scale.setScalar(s.glowSizeYards);
    g.add(glow);
  }
  return g;
}

function glowTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d')!;
  const gr = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(0.15, 'rgba(255,255,255,0.6)');
  gr.addColorStop(0.5, 'rgba(255,255,255,0.12)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gr;
  ctx.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}
